import React from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  selectActiveBrandGamificationItems,
  selectActiveBrandCampaign,
  activateGamificationItem,
  deactivateGamificationItem,
  userBrandsList,
  type ActivationInvoice,
} from '@/store/slices/casino-instance';
import { fetchInvoiceClientSecret } from '@/utils/stripe';
import {
  fetchOwnerWallets,
  fetchInvoices,
  payInvoiceFromWallet,
  cancelInvoice,
  buildPendingFeatureInvoiceMap,
} from '@/store/slices/wallet';
import { fetchConfiguratorConfig, type GamificationItemWithTiers } from '@/store/slices/configurator-config';
import type { CampaignItemTier, GamificationTier } from '@/components/modules/spark/configurator/types';

import { PageShell, EmptyState, Button, Icon, BackHeader, Toast } from '../../components';
import { BenchmarkBar } from './components/BenchmarkBar';
import { useLumioControl } from '../../layouts/MainLayout/lumioControl';
import { useToast } from '../../hooks/useToast';
import { COLORS, FEATURE_TONE_COLORS, type FeatureTone } from '../../theme/tokens';
import { useMountAnimation } from '../../hooks/useMountAnimation';
import {
  artBundleText,
  DEFAULT_CURRENCY,
  DEFAULT_LANG,
  pickTranslation,
  tierLabelFor,
  tierShortLabelFor,
  tierShowcaseHeader,
  tierShowcaseHighlights,
  visibleTiers,
} from '../../utils/catalog';
import { currencySymbol, formatAmountWithCode } from '../../utils/money';
import { campaignPrice, campaignScopeFor, type CampaignScope, planAvailability, showsDiscount } from './plans';
import { PaymentModal, type PaymentMethodId } from './components/PaymentModal';
import { FinishPaymentModal } from './components/FinishPaymentModal';
import { CancelUpgradeModal } from './components/CancelUpgradeModal';
import { FeatureShowcase } from './components/FeatureShowcase';
import { DeactivateModal } from './components/DeactivateModal';
import type { ProductDetail, ProductPlan, ShowcaseHighlight } from '../../mock-data/types';
import {
  HeaderRow,
  HeaderId,
  IconTile,
  IconArt,
  HeaderText,
  HeaderName,
  HeaderDesc,
  LumioPill,
  LumioSpark,
  LumioLabel,
  BenchmarkPanel,
  PanelEyebrow,
  BenchmarkTitle,
  ConfigEyebrow,
  SectionContainer,
  PlanRow,
  PlanCard,
  Check,
  PlanTop,
  PlanName,
  PlanPrice,
  PrevRow,
  PrevSpacer,
  PrevPrice,
  Savings,
  PriceRow,
  Currency,
  Amount,
  Term,
  DriftNote,
  SelectedPill,
  SelectedLabel,
  PendingLabel,
  Footer,
  FooterStatus,
  FooterActions,
  StatusChip,
  StatusDot,
  StatusLabel,
} from './product-detail.styles';

const LUMIO_SPARK_IMG = '/assets/product/lumio-spark.svg';

export type ProductDetailProps = {
  styleText?: string;
  className?: string;
};

type StatusView = {
  text: string;
  placeholders?: Record<string, string | number>;
  color: string;
  cta: string;
};

/** Comparison-bar tone derived from the feature's level state — mirrors the cards on the product root. */
const toneFor = (level: number | null, maxLevel: number): FeatureTone => {
  if (level === null) return 'inactive';
  if (level >= maxLevel) return 'maxed';
  return 'leveling';
};

/** Market-relative verdict for a tier at `level` (1-based) vs the market reference level, reusing
 *  the product's existing benchmark vocabulary. Mirrors the configurator find-my-pack captions so
 *  the headline "scrolls" through Below → Chasing → On track → Above → Leading as tiers are switched.
 *  A `null` level (no tier / empty ladder) keeps the missing-opportunity framing. */
const verdictFor = (level: number | null, marketLevel: number, maxLevel: number, t: TFunction): string => {
  if (level === null) return t('Missing opportunity');
  if (level >= maxLevel && level > marketLevel) return t('Leading the market');
  if (level > marketLevel) return t('Above market');
  if (level === marketLevel) return t('On track with the market');
  if (level === marketLevel - 1) return t('Chasing the market');
  return t('Below market');
};

/** Footer status + CTA derived from the feature's level + renewal state. Copy is
 *  translated reactively (`t` is passed in from the rendering component). An active
 *  feature has no up/downgrade path, so its action is Deactivate — unless its renewal
 *  is already cancelled (`renewing === false`), in which case it shows a "won't renew"
 *  status and the action is Reactivate (turn auto-renew back on). */
const statusFor = (
  level: number | null,
  maxLevel: number,
  renewing: boolean | null,
  expiresAt: string | null,
  lang: string,
  t: TFunction,
): StatusView => {
  if (level === null) return { text: t('Inactive'), color: COLORS.statusDanger, cta: t('Activate now') };
  // Cancelled but still running until it expires: nudge to reactivate.
  if (renewing === false) {
    const endsOn = expiresAt
      ? new Date(expiresAt).toLocaleDateString(lang, { month: 'long', day: 'numeric' })
      : '';
    return {
      text: endsOn ? t("Won't renew · ends {{date}}") : t("Won't renew"),
      placeholders: endsOn ? { date: endsOn } : undefined,
      color: COLORS.statusDanger,
      cta: t('Reactivate'),
    };
  }
  const placeholders = { level, max: maxLevel };
  const tone = level >= maxLevel ? FEATURE_TONE_COLORS.maxed : FEATURE_TONE_COLORS.leveling;
  return { text: t('Level {{level}}/{{max}}'), placeholders, color: tone, cta: t('Deactivate') };
};

/** Build a plan card by joining a catalog tier (art / label / showcase) with its price from
 *  the brand's active campaign (`casino-instance/list` → `campaign_data`), matched by tier id.
 *  `campaignTier` is that campaign tier (undefined when the campaign doesn't carry it) and
 *  `currency` the campaign's billing currency. The `/configurator` catalog's tier cost tables
 *  are stale placeholders and are NOT read here (see ./missing-data.md). Availability follows
 *  the catalog mask AND the campaign (see ./plans.ts). Copy (previous price, savings, billing
 *  term) is translated reactively via `t`. */
const planFromTier = (
  catalogItem: GamificationItemWithTiers,
  tier: GamificationTier,
  campaignTier: CampaignItemTier | undefined,
  scope: CampaignScope,
  currency: string,
  lang: string,
  t: TFunction,
): ProductPlan => {
  const price = campaignPrice(campaignTier);
  const availability = planAvailability(catalogItem, tier, campaignTier, scope);
  const { available, drift } = availability;
  // Both figures are defined whenever `hasDiscount` holds; narrowed here for the type checker. A
  // drift card shows no discount at all (it reads "Not priced yet" instead).
  const discount =
    showsDiscount(price, availability) && price.base !== undefined && price.discounted !== undefined
      ? { base: price.base, discounted: price.discounted }
      : null;

  return {
    id: String(tier.id),
    name: tierLabelFor(tier, catalogItem.name, lang),
    // The bar's pill states the tier alone: the page names the feature twice above it already.
    barLabel: tierShortLabelFor(tier, catalogItem, lang),
    prevPrice: discount
      ? t('{{symbol}}{{price}} per month', {
          symbol: currencySymbol(currency),
          price: formatAmountWithCode(discount.base, currency),
        })
      : null,
    savings: discount ? t('Save {{percent}}%', { percent: Math.round((1 - discount.discounted / discount.base) * 100) }) : null,
    // A tier the campaign doesn't price (absent, or 0) renders no figure; `drift` says why.
    currency: price.priced ? currency : '',
    amount: price.priced ? String(price.shown) : '',
    // Billing term/duration is not in the campaign — see ./missing-data.md.
    term: t('/month'),
    available,
    drift,
  };
};

/**
 * Product feature detail page (`/account/product/:id`), top to bottom: the feature
 * identity header with a Lumio prompt, the status + activate/deactivate action, the
 * You-vs-Market benchmark, the configuration (tier) picker, then the highlights +
 * demo-clip showcase. Center content only. Art / labels / showcase come from the
 * `/configurator` catalog (`configuratorConfig.gamificationItems`, resolved by the `:id`
 * URL param); the per-tier PRICING + billing currency come from the active brand's
 * price-engine campaign in `casino-instance/list` (`campaign_data`, joined by tier id) —
 * NOT the catalog, whose tier cost tables are stale placeholders. Both are joined with the
 * brand's purchase for the item; remaining gaps are tracked in ./missing-data.md.
 *
 * An enabled feature has no up/downgrade path: the picker is read-only and the action
 * is Deactivate (a confirmation dialog → the `deactivate-item` endpoint), or Reactivate
 * when its renewal is already cancelled. Activation (inactive features) goes through the
 * pay modal → `activate-item` (+ `pay-invoice` from the wallet). See ./missing-data.md.
 */
const ProductDetail = ({ styleText = '', className = '' }: ProductDetailProps): React.ReactElement => {
  const { id } = useParams();
  const itemId = Number(id);
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const lang = i18n.language || DEFAULT_LANG;
  const animate = useMountAnimation();
  const { askLumio } = useLumioControl();

  const { show: showToast, toast, dismiss: dismissToast } = useToast();

  const catalogItems = useAppSelector((state) => state.configuratorConfig.gamificationItems);
  const catalogLoaded = useAppSelector((state) => state.configuratorConfig.loaded);
  const purchases = useAppSelector(selectActiveBrandGamificationItems);
  // The active brand's id IS the casino_instance_id the activate/deactivate endpoints
  // take. Read from `displayBrand` (same source as the items) so it lags a brand switch
  // in lockstep with the page content.
  const casinoInstanceId = useAppSelector((state) => state.casinoInstance.displayBrand?.id);
  // The brand's active price-engine campaign — the source of the real per-tier prices +
  // billing currency (reads `displayBrand`, so it lags a brand switch like the items).
  const campaignData = useAppSelector(selectActiveBrandCampaign);

  React.useEffect(() => {
    dispatch(fetchConfiguratorConfig());
    // Owner invoices back the "Finish payment" state (a feature begun but never paid for).
    dispatch(fetchInvoices());
  }, [dispatch]);

  // The open (unpaid) invoice for THIS feature on the active brand, if any: the owner started
  // activating / upgrading it but never finished paying. Keyed by catalog item id + brand-scoped
  // so another brand's pending invoice can't leak here. Present → the page swaps its tier picker
  // + Activate flow for a read-only "Finish payment" one (the invoice already fixes the tier).
  const invoices = useAppSelector((state) => state.ownerWallet.invoices);
  const pendingInvoice = React.useMemo(
    () => buildPendingFeatureInvoiceMap(invoices, casinoInstanceId).get(itemId),
    [invoices, casinoInstanceId, itemId],
  );
  // The tier the pending invoice pays for (its `new_item.tier_id`), as a plan id string.
  const pendingTierId = pendingInvoice?.invoice_data?.new_item?.tier_id;
  const pendingTierIdStr = pendingTierId !== undefined ? String(pendingTierId) : '';

  const detail = React.useMemo<ProductDetail | undefined>(() => {
    const catalogItem = catalogItems.find((entry) => entry.id === itemId);
    if (!catalogItem) {
      return undefined;
    }

    const bundle = catalogItem.art_bundle?.[0];
    const purchase = purchases.find((item) => item.item_id === catalogItem.id);
    // The page's ladder follows the catalog MASK (`visibleTiers`, shared with the product root
    // cards so both pages agree on a feature's level / max): the item's active, non-off rungs,
    // plus the tier the brand owns and the tier a pending invoice pays for even when those are
    // retired. A retired rung nobody holds is not offered, so it is not shown. The ids are typed
    // `string | number` upstream but already numeric at runtime (the selectors coerce them); the
    // `Number()` only satisfies the type.
    const ladder = visibleTiers(
      catalogItem,
      purchase !== undefined ? Number(purchase.tier_id) : undefined,
      pendingTierId !== undefined ? Number(pendingTierId) : undefined,
    );
    const tierIndex = purchase ? ladder.findIndex((tier) => tier.id === purchase.tier_id) : -1;
    const level = tierIndex >= 0 ? tierIndex + 1 : null;
    const maxLevel = ladder.length;
    // Market reference = middle of the ladder until real market data exists (see ./missing-data.md).
    const marketLevel = Math.ceil(maxLevel / 2);
    const marketProgress = maxLevel > 0 ? Math.round((marketLevel / maxLevel) * 100) : 0;
    // 1-based level of every tier by plan id — lets the benchmark bar preview whichever tier the
    // picker has selected (its "You" position slides on a tier switch, like the find-my-pack bar).
    const tierLevels: Record<string, number> = {};
    ladder.forEach((tier, index) => {
      tierLevels[String(tier.id)] = index + 1;
    });

    // Real per-tier pricing: the matching item in the brand's active campaign, keyed by
    // numeric catalog id (same id space as the ladder tiers). The campaign carries the
    // billing currency; fall back to its `currency_code` then USD if absent.
    const campaignItem = campaignData?.campaign.items.find((entry) => entry.id === itemId);
    // Key by numeric tier id (campaign ids may arrive as strings) so the ladder join is exact.
    const campaignTierById = new Map<number, CampaignItemTier>(
      (campaignItem?.tiers ?? []).map((campaignTier) => [Number(campaignTier.id), campaignTier]),
    );
    const currency = campaignData?.currency ?? campaignData?.campaign.currency_code ?? DEFAULT_CURRENCY;

    const defaultPlanId = tierIndex >= 0 ? String(ladder[tierIndex].id) : '';
    // Whether the brand has a bound campaign at all, and whether it carries + enables this item.
    // Without a campaign nothing is drift: the tiers are simply unpriced (see ./missing-data.md).
    const scope = campaignScopeFor(campaignData !== undefined, campaignItem);
    // Every visible tier renders. A tier the catalog offers but the bound campaign does not sell is
    // configuration drift: it renders disabled with a "Not priced yet" note (`plan.drift`) instead
    // of vanishing, so the gap is visible and gets fixed on the campaign side.
    const plans = ladder.map((tier) =>
      planFromTier(catalogItem, tier, campaignTierById.get(tier.id), scope, currency, lang, t),
    );

    return {
      id: String(catalogItem.id),
      image: bundle?.logo ?? '',
      name: artBundleText(bundle, lang) ?? catalogItem.name,
      description: pickTranslation(bundle?.properties?.description, lang) ?? '',
      benchmark: {
        // Verdict copy derived from state until real market data exists — see ./missing-data.md.
        label: level === null ? t('Missing opportunity') : t('On track with the market'),
        value: level === null ? 0 : Math.round((level / maxLevel) * 100),
        market: marketProgress,
      },
      level,
      maxLevel,
      tierLevels,
      marketLevel,
      plans,
      defaultPlanId,
      // Renewal state of the owned tier: drives the "won't renew" status + Reactivate CTA.
      renewing: purchase ? purchase.renew : null,
      expiresAt: purchase?.expires_at ?? null,
    };
  }, [catalogItems, purchases, itemId, campaignData, pendingTierId, lang, t]);

  const [selectedPlanId, setSelectedPlanId] = React.useState<string>('');
  const [paymentOpen, setPaymentOpen] = React.useState(false);
  const [finishPaymentOpen, setFinishPaymentOpen] = React.useState(false);
  const [cancelUpgradeOpen, setCancelUpgradeOpen] = React.useState(false);
  const [deactivateOpen, setDeactivateOpen] = React.useState(false);
  // In-flight guard for the activate / deactivate calls — disables the modal CTA and
  // blocks a double submit until the request (and the follow-up brand refetch) settle.
  const [submitting, setSubmitting] = React.useState(false);

  // The activation invoice minted this modal session (see ensureActivation) — cached so the wallet
  // and card paths settle the SAME invoice, and read on close to surface an abandoned/unpaid one.
  const mintedActivationRef = React.useRef<ActivationInvoice | null>(null);

  // Default picker selection: the owned tier when the feature is already active, otherwise
  // the top AVAILABLE tier (the highest tier the campaign prices + enables, skipping a drift
  // one so the CTA opens on an activatable choice). With nothing purchasable it falls back to the
  // last non-drift plan (a drift card would render highlighted AND greyed at once), and with only
  // drift plans selects nothing; the CTA stays disabled either way. Resolved to a plan ID (stable
  // across language changes) so re-rendering in another language never resets a pick.
  const initialPlanId = React.useMemo(() => {
    if (!detail) return '';
    // A pending payment fixes the tier, so lead with it: the read-only picker highlight and the
    // showcase below (both track selectedPlanId) then agree on the tier the invoice pays for.
    if (pendingTierIdStr && detail.plans.some((plan) => plan.id === pendingTierIdStr)) return pendingTierIdStr;
    if (detail.defaultPlanId) return detail.defaultPlanId;
    const topAvailable = [...detail.plans].reverse().find((plan) => plan.available);
    const lastNonDrift = [...detail.plans].reverse().find((plan) => !plan.drift);
    return topAvailable?.id ?? lastNonDrift?.id ?? '';
  }, [detail, pendingTierIdStr]);

  // The detail resolves asynchronously (catalog fetch) — sync the selection when it lands.
  React.useEffect(() => {
    setSelectedPlanId(initialPlanId);
  }, [initialPlanId]);

  // The showcase (talking points + demo clips that replace the static hero) tracks the
  // selected configuration: it shows the highlights + per-highlight video of the chosen
  // tier (the owned tier for an enabled feature, else the first tier as a preview).
  // Resolved from the same catalog the page already holds — see ../../utils/catalog.
  const showcase = React.useMemo<
    { eyebrow: string; title: string; highlights: ShowcaseHighlight[]; resetKey: string } | undefined
  >(() => {
    const catalogItem = catalogItems.find((entry) => entry.id === itemId);
    if (!catalogItem) {
      return undefined;
    }
    // The same mask-aware ladder as the picker, so the preview never lands on a rung it hides.
    const purchase = purchases.find((item) => item.item_id === catalogItem.id);
    const ladder = visibleTiers(
      catalogItem,
      purchase !== undefined ? Number(purchase.tier_id) : undefined,
      pendingTierId !== undefined ? Number(pendingTierId) : undefined,
    );
    if (ladder.length === 0) {
      return undefined;
    }
    const tier = ladder.find((entry) => String(entry.id) === selectedPlanId) ?? ladder[0];
    const header = tierShowcaseHeader(tier, lang);
    const feature = artBundleText(catalogItem.art_bundle?.[0], lang) ?? catalogItem.name;
    return {
      eyebrow: header?.eyebrow || feature,
      title: header?.title || t('How it works?'),
      highlights: tierShowcaseHighlights(tier, lang),
      resetKey: `${catalogItem.id}-${tier.id}`,
    };
  }, [catalogItems, purchases, itemId, pendingTierId, selectedPlanId, lang, t]);

  const onChoosePlan = React.useCallback((planId: string) => {
    if (planId) setSelectedPlanId(planId);
  }, []);

  // Open Lumio (docked column / drawer / mobile overlay, per breakpoint) and ask
  // it about this feature. The prompt is localized — same key as the pill label —
  // so the user's turn reads naturally in the Settings language.
  const onTellMore = React.useCallback(() => {
    if (!detail) return;
    askLumio(t('Tell me more about {{name}}', { name: detail.name }));
  }, [askLumio, t, detail]);

  const onActivate = React.useCallback(() => {
    setPaymentOpen(true);
  }, []);

  const onPaymentClose = React.useCallback(() => {
    if (submitting) return;
    // Grab fresh data on close: if activation minted an invoice this session that wasn't settled
    // (e.g. the owner dropped an in-flight card payment by closing the modal), the refetch surfaces
    // it as a pending "Finish payment" state instead of silently leaving it behind.
    const mintedUnsettled = mintedActivationRef.current !== null;
    setPaymentOpen(false);
    if (mintedUnsettled) {
      void Promise.all([dispatch(fetchInvoices()), dispatch(userBrandsList()), dispatch(fetchOwnerWallets())]);
    }
  }, [submitting, dispatch]);

  // Mint the activation invoice AT MOST ONCE per open modal, caching the result so the wallet and
  // card paths (and a card retry after a decline, or "prepared card then paid with wallet") settle
  // the SAME invoice instead of minting duplicates. `mintedActivationRef` is declared with the
  // other state above (onPaymentClose reads it); cleared when the modal closes / the tier changes.
  // `activate-item` mints the invoice + its PaymentIntent; the feature stays "payment pending"
  // until that invoice is settled (wallet or card).
  React.useEffect(() => {
    mintedActivationRef.current = null;
  }, [paymentOpen, selectedPlanId]);

  const ensureActivation = React.useCallback(async (): Promise<ActivationInvoice> => {
    if (mintedActivationRef.current) return mintedActivationRef.current;
    const tierId = Number(selectedPlanId);
    if (!casinoInstanceId || !Number.isFinite(tierId)) {
      throw new Error('missing activation context');
    }
    const result = await dispatch(
      activateGamificationItem({ casino_instance_id: casinoInstanceId, item_id: itemId, tier_id: tierId }),
    ).unwrap();
    mintedActivationRef.current = result;
    return result;
  }, [dispatch, casinoInstanceId, itemId, selectedPlanId]);

  // Card path: the modal calls this on the first "Pay" click — mint the invoice, then resolve its
  // Stripe PaymentIntent client secret (inline when the backend returns it, else fetched via the
  // invoice's session). The modal binds the card form to this real intent, so no orphaned deferred
  // payment / unpaid invoice is left behind. Deliberately does NOT refetch invoices here — that
  // would flip the page into the "Finish payment" state and unmount the modal mid-charge.
  const createCardIntent = React.useCallback(async (): Promise<string> => {
    const result = await ensureActivation();
    const inlineSecret = result.provider_reference ?? result.client_secret;
    if (inlineSecret) return inlineSecret;
    if (result.session_id) return fetchInvoiceClientSecret(result.session_id);
    throw new Error('activation returned no payment intent');
  }, [ensureActivation]);

  // After a CARD charge, the invoice flips pending → paid server-side via the Stripe webhook,
  // which lags the in-browser confirm by a few seconds (same lag the wallet-deposit flow handles).
  // The single immediate refetch therefore still sees the invoice `pending`, which would briefly
  // re-surface the "Finish payment" footer on an already-charged card and let the owner settle it a
  // SECOND time from the wallet (a double-charge). Re-poll a couple of times so the paid invoice
  // leaves the pending set without a manual refresh. Wallet settlement is synchronous, so it does
  // not need this.
  const repollServerTruthAfterCard = React.useCallback(() => {
    const refresh = () => {
      dispatch(userBrandsList());
      dispatch(fetchOwnerWallets());
      dispatch(fetchInvoices());
    };
    window.setTimeout(refresh, 3000);
    window.setTimeout(refresh, 8000);
  }, [dispatch]);

  // Wallet path: mint (or reuse) the invoice, settle it from the MAIN balance, then refetch + close.
  const onPayWithWallet = React.useCallback(async () => {
    if (submitting) return;
    const featureName = detail?.name ?? '';
    setSubmitting(true);
    try {
      const result = await ensureActivation();
      // The activation response IS the pending invoice — its own `id` is the payable id (there is
      // no `invoice_id`). Settle it from the MAIN balance.
      if (result.id) {
        await dispatch(payInvoiceFromWallet({ invoice_id: result.id })).unwrap();
      }
      // Refetch the brand list (item's active/renew/tier), the wallet balance, and the invoices
      // (the settled one drops out of the pending set) so the page + top bar reflect the change.
      await Promise.all([dispatch(userBrandsList()), dispatch(fetchOwnerWallets()), dispatch(fetchInvoices())]);
      setPaymentOpen(false);
      showToast(t('{{name}} is now active', { name: featureName }), { icon: 'check' });
    } catch {
      showToast(t("Couldn't activate {{name}}. Please try again.", { name: featureName }));
    } finally {
      setSubmitting(false);
    }
  }, [dispatch, ensureActivation, submitting, detail, showToast, t]);

  // Card path: fired after the modal's in-modal Stripe confirm succeeds — the invoice is charged,
  // so just refetch + close (mirrors the finish-payment card branch).
  const onCardPaid = React.useCallback(async () => {
    const featureName = detail?.name ?? '';
    setSubmitting(true);
    try {
      await Promise.all([dispatch(userBrandsList()), dispatch(fetchOwnerWallets()), dispatch(fetchInvoices())]);
      setPaymentOpen(false);
      showToast(t('{{name}} is now active', { name: featureName }), { icon: 'check' });
      // Clear the pending invoice once the webhook settles the card charge (see helper).
      repollServerTruthAfterCard();
    } catch {
      // The card charge already went through; don't strand the owner or invite a second charge.
      setPaymentOpen(false);
      showToast(t('Payment received for {{name}}. Reload to see it live.', { name: featureName }), { icon: 'check' });
      repollServerTruthAfterCard();
    } finally {
      setSubmitting(false);
    }
  }, [dispatch, detail, showToast, t, repollServerTruthAfterCard]);

  // "Finish payment" for a feature with an open invoice: the invoice already fixes the tier +
  // amount, so this settles it rather than activating from scratch. The modal offers the wallet
  // (pay-invoice) and card (a real Stripe confirm on the invoice's session intent) paths.
  const onFinishPayment = React.useCallback(() => {
    setFinishPaymentOpen(true);
  }, []);

  const onFinishPaymentClose = React.useCallback(() => {
    if (submitting) return;
    setFinishPaymentOpen(false);
  }, [submitting]);

  const onFinishPay = React.useCallback(
    async (method: PaymentMethodId) => {
      if (!pendingInvoice || submitting) return;
      const featureName = detail?.name ?? '';

      setSubmitting(true);
      try {
        // Wallet settles the invoice from the MAIN balance (throws → caught → modal stays open to
        // retry). The card path is ALREADY charged inside the modal by the time it calls back, so
        // here we only refetch.
        if (method === 'wallet') {
          await dispatch(payInvoiceFromWallet({ invoice_id: pendingInvoice.id })).unwrap();
        }
        // Refetch the brand list (the paid feature turns active), the wallet balance, and the
        // invoices (the settled one drops out of the pending set) so the page + top bar update.
        await Promise.all([
          dispatch(userBrandsList()),
          dispatch(fetchOwnerWallets()),
          dispatch(fetchInvoices()),
        ]);
        setFinishPaymentOpen(false);
        showToast(t('{{name}} is now active', { name: featureName }), { icon: 'check' });
        // Card settles via the lagging Stripe webhook; re-poll so the invoice leaves the pending
        // set and can't be paid a second time from the wallet (see helper). Wallet is synchronous.
        if (method === 'card') repollServerTruthAfterCard();
      } catch {
        if (method === 'card') {
          // The card charge already went through; don't strand the owner on a stuck CTA or invite
          // a second charge. Close the modal and note it's paid but the view couldn't refresh.
          setFinishPaymentOpen(false);
          showToast(t('Payment received for {{name}}. Reload to see it live.', { name: featureName }), { icon: 'check' });
          repollServerTruthAfterCard();
        } else {
          showToast(t("Couldn't finish the payment for {{name}}. Please try again.", { name: featureName }));
        }
      } finally {
        setSubmitting(false);
      }
    },
    [dispatch, pendingInvoice, submitting, detail, showToast, t, repollServerTruthAfterCard],
  );

  // "Cancel upgrade": void the pending invoice via `invoice/cancel` (terminal). The feature
  // stays where it is and nothing is charged; on success the invoice drops out of the pending
  // set, so the page reverts to its normal activate/deactivate state.
  const onCancelUpgrade = React.useCallback(() => {
    setCancelUpgradeOpen(true);
  }, []);

  const onCancelUpgradeClose = React.useCallback(() => {
    if (submitting) return;
    setCancelUpgradeOpen(false);
  }, [submitting]);

  const onCancelUpgradeConfirm = React.useCallback(async () => {
    if (!pendingInvoice || submitting) {
      setCancelUpgradeOpen(false);
      return;
    }
    const featureName = detail?.name ?? '';

    setSubmitting(true);
    try {
      await dispatch(cancelInvoice({ invoice_id: pendingInvoice.id })).unwrap();
      await dispatch(fetchInvoices());
      setCancelUpgradeOpen(false);
      showToast(t('Upgrade cancelled for {{name}}', { name: featureName }), { icon: 'check' });
    } catch {
      showToast(t("Couldn't cancel the upgrade for {{name}}. Please try again.", { name: featureName }));
    } finally {
      setSubmitting(false);
    }
  }, [dispatch, pendingInvoice, submitting, detail, showToast, t]);

  const onDeactivate = React.useCallback(() => {
    setDeactivateOpen(true);
  }, []);

  const onDeactivateClose = React.useCallback(() => {
    if (submitting) return;
    setDeactivateOpen(false);
  }, [submitting]);

  const onDeactivateConfirm = React.useCallback(async () => {
    // deactivate-item takes the INSTANCE-scoped record id (UUID), not the catalog id.
    const purchase = purchases.find((item) => item.item_id === itemId);
    if (!casinoInstanceId || !purchase || submitting) {
      setDeactivateOpen(false);
      return;
    }
    const featureName = detail?.name ?? '';

    setSubmitting(true);
    try {
      await dispatch(
        deactivateGamificationItem({ casino_instance_id: casinoInstanceId, item_id: purchase.id }),
      ).unwrap();
      await dispatch(userBrandsList());
      setDeactivateOpen(false);
      // Cancel-at-period-end: the feature keeps running until it expires, then stops renewing.
      showToast(t('{{name}} will turn off at the end of your billing period', { name: featureName }), {
        icon: 'check',
      });
    } catch {
      showToast(t("Couldn't deactivate {{name}}. Please try again.", { name: featureName }));
    } finally {
      setSubmitting(false);
    }
  }, [dispatch, purchases, itemId, casinoInstanceId, submitting, detail, showToast, t]);

  // Reactivate a cancelled-but-still-active feature: a same-tier renewal of the owned
  // tier (turns auto-renew back on). No payment step — activation is direct and billed on
  // the recurring cycle — so this fires immediately without the pay modal.
  const onReactivate = React.useCallback(async () => {
    const purchase = purchases.find((item) => item.item_id === itemId);
    const tierId = Number(purchase?.tier_id);
    if (!casinoInstanceId || !purchase || !Number.isFinite(tierId) || submitting) return;
    const featureName = detail?.name ?? '';

    setSubmitting(true);
    try {
      await dispatch(
        activateGamificationItem({ casino_instance_id: casinoInstanceId, item_id: itemId, tier_id: tierId }),
      ).unwrap();
      await dispatch(userBrandsList());
      showToast(t('{{name}} will keep renewing', { name: featureName }), { icon: 'check' });
    } catch {
      showToast(t("Couldn't reactivate {{name}}. Please try again.", { name: featureName }));
    } finally {
      setSubmitting(false);
    }
  }, [dispatch, purchases, itemId, casinoInstanceId, submitting, detail, showToast, t]);

  if (!detail) {
    return (
      <PageShell styleText={styleText} className={className}>
        {catalogLoaded ? <EmptyState text={t('This feature is not available.')} /> : null}
      </PageShell>
    );
  }

  // An unpaid invoice for this feature overrides the normal activate/deactivate flow: the
  // payment (not the tier choice) is what's left, so the footer shows "Payment pending" + a
  // "Finish payment" CTA and the picker turns read-only around the invoice's tier.
  const pending = pendingInvoice !== undefined;
  const status: StatusView = pending
    ? { text: t('Payment pending'), color: COLORS.warning, cta: t('Finish payment') }
    : statusFor(detail.level, detail.maxLevel, detail.renewing, detail.expiresAt, lang, t);
  // An owned feature whose renewal is cancelled (still running until it expires): the
  // footer action turns auto-renew back on instead of deactivating.
  const cancelled = !pending && detail.level !== null && detail.renewing === false;
  // An enabled feature has no up/downgrade path: the only action is Deactivate. The
  // configuration picker stays visible but read-only — the owned tier is marked
  // "Current configuration", the others are greyed and inert.
  const enabled = detail.level !== null;
  const selectedPlan = detail.plans.find((plan) => plan.id === selectedPlanId) ?? detail.plans[0];
  // Only an activatable (campaign-priced + enabled) selection can be paid for — gates the CTA.
  const canActivate = selectedPlan?.available ?? false;
  // The benchmark bar tells a three-point story (like the configurator's find-my-pack panel):
  //  · "You (now)" — where the feature stands today: the owned tier level, or 0 when inactive / at
  //    a lower tier. A fixed marker, so your current position stays visible while you shop tiers.
  //  · "Could be" — where the SELECTED tier would put you: the projection fill + tier pill + the
  //    headline verdict, which slide as the picker switches tier.
  //  · "Market" — the fixed reference marker.
  const nowLevel = detail.level;
  const nowValue = detail.maxLevel > 0 && nowLevel !== null ? Math.round((nowLevel / detail.maxLevel) * 100) : 0;
  const nowTone = toneFor(nowLevel, detail.maxLevel);
  // Falls back to the owned level before the async selection settles.
  const couldBeLevel = detail.tierLevels[selectedPlanId] ?? nowLevel;
  const couldBeValue =
    detail.maxLevel > 0 && couldBeLevel !== null ? Math.round((couldBeLevel / detail.maxLevel) * 100) : 0;
  const couldBeVerdict = verdictFor(couldBeLevel, detail.marketLevel, detail.maxLevel, t);
  // The plan the pending invoice pays for + the committed amount to settle (from the invoice,
  // not the catalog — the price was locked in when the invoice was minted).
  const pendingPlan = pending ? detail.plans.find((plan) => plan.id === pendingTierIdStr) : undefined;
  const pendingSessionId = pendingInvoice?.session_id ?? pendingInvoice?.invoice_data?.session_id ?? null;

  const renderPlan = (plan: ProductPlan): React.ReactElement => {
    const owned = plan.id === detail.defaultPlanId;
    // The tier a pending invoice will activate — highlighted (read-only) while payment is due.
    const pendingTier = pending && plan.id === pendingTierIdStr;
    // The picker is read-only both when the feature is enabled (no up/downgrade) AND while a
    // payment is pending (the invoice fixes the tier).
    const readOnly = enabled || pending;
    const selected = pending ? pendingTier : enabled ? owned : plan.id === selectedPlanId;
    // Before activation a tier is selectable only when the campaign prices + enables it, so a
    // disabled tier (e.g. a not-yet-available Ultra) can't be picked or paid for. Once the
    // feature is enabled (or a payment is pending) the picker turns read-only: the highlighted
    // tier keeps its pill, the rest grey.
    const interactive = !readOnly && plan.available;
    const dim = pending ? !pendingTier : enabled ? !owned : !plan.available;
    const cardProps = interactive
      ? {
          as: 'button' as const,
          type: 'button' as const,
          onClick: () => onChoosePlan(plan.id),
          'aria-pressed': selected,
        }
      : { as: 'div' as const };
    return (
      <PlanCard key={plan.id} $selected={selected} $dim={dim} $interactive={interactive} {...cardProps}>
        <PlanTop>
          <PlanName text={plan.name} tag="span" $selected={selected} />
          <PlanPrice>
            {plan.prevPrice ? (
              <PrevRow>
                <PrevPrice text={plan.prevPrice} tag="span" />
                {plan.savings ? <Savings text={plan.savings} tag="span" /> : null}
              </PrevRow>
            ) : (
              <PrevSpacer />
            )}
            {/* Drift (catalog offers the tier, the campaign doesn't sell it): say so. An unpriced
                non-drift plan (an owned retired rung, or no campaign yet) shows no figure and no
                bare "/month" either. */}
            <PriceRow>
              {plan.drift ? (
                <DriftNote text="Not priced yet" tag="span" />
              ) : plan.amount ? (
                <>
                  <Currency>{currencySymbol(plan.currency)}</Currency>
                  <Amount>{formatAmountWithCode(Number(plan.amount), plan.currency)}</Amount>
                  <Term text={plan.term} tag="span" />
                </>
              ) : null}
            </PriceRow>
          </PlanPrice>
        </PlanTop>
        {interactive && selected ? (
          <Check>
            <Icon name="check" size={14} color={COLORS.onAccent} />
          </Check>
        ) : null}
        {pendingTier ? (
          <SelectedPill>
            <Icon name="info" size={16} color={COLORS.warning} />
            <PendingLabel text="Payment pending" tag="span" />
          </SelectedPill>
        ) : enabled && owned ? (
          <SelectedPill>
            <Icon name="check" size={16} color={COLORS.benefitPositive} />
            <SelectedLabel text="Current configuration" tag="span" />
          </SelectedPill>
        ) : null}
      </PlanCard>
    );
  };

  return (
    <PageShell styleText={styleText} className={className}>
      <BackHeader title={detail.name} />

      <HeaderRow>
        <HeaderId>
          <IconTile>
            <IconArt src={detail.image} alt="" />
          </IconTile>
          <HeaderText>
            <HeaderName text={detail.name} tag="span" />
            {detail.description ? <HeaderDesc text={detail.description} tag="span" /> : null}
          </HeaderText>
        </HeaderId>
        <LumioPill type="button" onClick={onTellMore}>
          <LumioSpark src={LUMIO_SPARK_IMG} alt="" />
          <LumioLabel text="Tell me more about {{name}}" placeholders={{ name: detail.name }} tag="span" />
        </LumioPill>
      </HeaderRow>

      <BenchmarkPanel>
        <PanelEyebrow text="Feature benchmark" tag="span" />
        <BenchmarkTitle text={couldBeVerdict} tag="span" />
        <BenchmarkBar
          now={nowValue}
          couldBe={couldBeValue}
          market={detail.benchmark.market}
          nowTone={nowTone}
          tierLabel={selectedPlan?.barLabel ?? ''}
          animate={animate}
        />
      </BenchmarkPanel>

      <section>
        <ConfigEyebrow text="{{name}} configurations" placeholders={{ name: detail.name }} tag="span" />
        <SectionContainer>
          <PlanRow>{detail.plans.map(renderPlan)}</PlanRow>
          <Footer>
            <FooterStatus>
              <PanelEyebrow text="Feature status" tag="span" />
              <StatusChip>
                <StatusDot $color={status.color} />
                <StatusLabel text={status.text} placeholders={status.placeholders} tag="span" $color={status.color} />
              </StatusChip>
            </FooterStatus>
            {pending ? (
              <FooterActions>
                <Button
                  variant="outline"
                  label={t('Cancel upgrade')}
                  disabled={submitting}
                  onClick={onCancelUpgrade}
                />
                <Button variant="primary" label={status.cta} disabled={submitting} onClick={onFinishPayment} />
              </FooterActions>
            ) : (
              <Button
                variant={enabled && !cancelled ? 'outline' : 'primary'}
                label={status.cta}
                // Activation needs a campaign-priced, enabled selection; deactivate / reactivate
                // of an owned feature is always allowed.
                disabled={submitting || (!enabled && !canActivate)}
                onClick={cancelled ? onReactivate : enabled ? onDeactivate : onActivate}
              />
            )}
          </Footer>
          {showcase ? (
            <FeatureShowcase
              highlights={showcase.highlights}
              eyebrow={showcase.eyebrow}
              title={showcase.title}
              resetKey={showcase.resetKey}
              fallbackImage={detail.image}
              fallbackName={detail.name}
            />
          ) : null}
        </SectionContainer>
      </section>

      {!enabled && !pending && selectedPlan ? (
        <PaymentModal
          open={paymentOpen}
          image={detail.image}
          name={detail.name}
          tierName={selectedPlan.name}
          amount={selectedPlan.amount}
          currency={selectedPlan.currency}
          prevPrice={selectedPlan.prevPrice}
          savings={selectedPlan.savings}
          busy={submitting}
          onClose={onPaymentClose}
          onPayWithWallet={onPayWithWallet}
          createCardIntent={createCardIntent}
          onCardPaid={onCardPaid}
        />
      ) : null}

      {pending && pendingInvoice ? (
        <>
          <FinishPaymentModal
            open={finishPaymentOpen}
            image={detail.image}
            name={detail.name}
            tierName={pendingPlan?.name ?? detail.name}
            amount={Number(pendingInvoice.price) || 0}
            currency={pendingInvoice.currency}
            sessionId={pendingSessionId}
            busy={submitting}
            onClose={onFinishPaymentClose}
            onPay={onFinishPay}
          />
          <CancelUpgradeModal
            open={cancelUpgradeOpen}
            name={detail.name}
            tierName={pendingPlan?.name ?? detail.name}
            amount={`${currencySymbol(pendingInvoice.currency)}${formatAmountWithCode(
              Number(pendingInvoice.price) || 0,
              pendingInvoice.currency,
            )}`}
            busy={submitting}
            onClose={onCancelUpgradeClose}
            onConfirm={onCancelUpgradeConfirm}
          />
        </>
      ) : null}

      <DeactivateModal
        open={deactivateOpen}
        name={detail.name}
        busy={submitting}
        onClose={onDeactivateClose}
        onConfirm={onDeactivateConfirm}
      />

      <Toast toast={toast} onDismiss={dismissToast} />
    </PageShell>
  );
};

export default ProductDetail;